<!--
  Tutoring for families and tutors. A family sees each student's rate (gold badge if it's a
  grandfathered rate), hours left, what they owe, packages with both prices, sessions and lessons
  coming up (Miles schedules them). A tutor sees their own lessons, every lesson of their paired
  students and the lessons and students waiting for a tutor, which they take (right away with a
  market price, or by asking Miles); they mark lessons done, log exact times, and see their pay.
-->
<script lang="ts">
  import * as H from "../../settings/hints.ts";
  import * as A from "../../settings/amounts.ts";
  import BalanceAmount from "../../components/BalanceAmount.svelte";
  import PersonCard from "../../components/PersonCard.svelte";
  import { fill, shows } from "../../settings/index.ts";
  import * as TT from "../../settings/tutoring.ts";
  import StudentCard from "./StudentCard.svelte";
  import {
    DEFAULT_TUTOR_PAY_CENTS,
    hoursText,
    lessonProgress,
    packageSavings,
    studentSummary,
    personOverview,
    tenure,
    tutorSees,
    tutorSource,
    tutorSummary,
    type Tutor,
    type TutoringClaim,
    type TutoringEntry,
    type TutoringPackage,
    type TutoringSlot,
    type TutoringStudent,
  } from "@split/core";
  import { api } from "../../lib/api.ts";
  import { lessonLookup, localNow } from "../../lib/tutoring.svelte.ts";
  import { app } from "../../lib/store.svelte.ts";
  import { formatCents, shortDate } from "../../lib/format.ts";
  import Calendar, { type CalendarSource } from "./Calendar.svelte";
  import LessonProgress from "./LessonProgress.svelte";

  interface Mine {
    students: TutoringStudent[];
    tutors: Tutor[];
    packages: TutoringPackage[];
    entries: TutoringEntry[];
    slots: TutoringSlot[];
    names: Record<string, string>;
    taught?: { id: string; tutorId: string; name: string; className: string }[];
    pending?: { kind: "student" | "tutor"; id: string; name: string; family: string }[];
    /** Students looking for a tutor, for each of your tutor records. */
    open?: { tutorId: string; id: string; name: string; className: string; marketPayCents: number; lessons: number }[];
    claims?: TutoringClaim[];
    /** Your pay per hour for each of your students. */
    pay?: { tutorId: string; studentId: string; payCents: number }[];
  }
  let data = $state<Mine | null>(null);
  /** Her pay card is open unless she folds it. */
  let foldedPay = $state(false);
  let error = $state("");

  async function load() {
    try {
      data = await api<Mine>(`/api/me/tutoring?now=${localNow()}`);
      error = "";
    } catch (e) {
      error = (e as Error).message;
    }
  }
  $effect(() => {
    void load();
  });

  const name = (id: string | null) => (id && data?.names[id]) || "";
  const when = (dt: string) =>
    `${new Date(`${dt.slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" })} ${dt.slice(11, 16)}`;
  const month = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
  /** Los Angeles time now: lesson times are Los Angeles times. */
  const nowIso = localNow();

  async function confirm(p: { kind: "student" | "tutor"; id: string }, yes: boolean) {
    try {
      await api("/api/me/tutoring/confirm", { body: { kind: p.kind, id: p.id, yes } });
      app.toast(yes ? "Thanks, you're all set" : "Okay, we've let Miles know it isn't you");
      await load();
    } catch (e) {
      app.toast((e as Error).message, "error");
    }
  }

  // ---- a tutor: taking lessons and students, exact times, done or not done
  /** Any tutor step: sent, then everything reloaded. */
  async function step(path: string, body: Record<string, unknown>, done: string) {
    try {
      const r = await api<{ result?: "taken" | "asked" }>(path, { body });
      app.toast(r?.result === "asked" ? TT.CLAIM_ASKED_TOAST : done);
      await load();
    } catch (e) {
      app.toast((e as Error).message, "error");
      await load();
    }
  }
  const takeLesson = (t: Tutor, slot: TutoringSlot) => step("/api/me/tutoring/claim", { tutorId: t.id, slotId: slot.id }, TT.CLAIM_TAKEN_TOAST);
  const takeStudent = (t: Tutor, studentId: string) => step("/api/me/tutoring/claim", { tutorId: t.id, studentId }, TT.CLAIM_TAKEN_TOAST);
  const withdraw = (claimId: string) => step(`/api/me/tutoring/claims/${claimId}/withdraw`, {}, TT.CLAIM_WITHDRAW);
  /** Your request still waiting for a lesson or a student. */
  const waitingFor = (t: Tutor, k: { slotId?: string; studentId?: string }) =>
    (data?.claims ?? []).find(
      (c) => c.tutorId === t.id && c.status === "pending" && (k.slotId ? c.kind === "lesson" && c.slotId === k.slotId : c.kind === "student" && c.studentId === k.studentId),
    )?.id ?? null;
  const STATUS: Record<TutoringClaim["status"], string> = {
    pending: TT.REQUEST_STATUS_PENDING,
    approved: TT.REQUEST_STATUS_APPROVED,
    declined: TT.REQUEST_STATUS_DECLINED,
    withdrawn: TT.REQUEST_STATUS_WITHDRAWN,
  };
  const payOf = (t: Tutor, studentId: string) => (data?.pay ?? []).find((p) => p.tutorId === t.id && p.studentId === studentId)?.payCents ?? DEFAULT_TUTOR_PAY_CENTS;

  async function done(slot: TutoringSlot, yes: boolean, early = false) {
    try {
      await api("/api/me/tutoring/done", { body: { slotId: slot.id, done: yes, early } });
      app.toast(yes ? "Marked done" : "Marked not done");
      await load();
    } catch (e) {
      app.toast((e as Error).message, "error");
    }
  }

  /**
   * The same calendar you see, with this tutor's lessons, every lesson of their paired students
   * and the lessons waiting for a tutor (packages/core tutorSees).
   */
  function calendarFor(t: Tutor): CalendarSource {
    const paired = () => (data?.taught ?? []).map((x) => ({ id: x.id, tutorId: x.tutorId }));
    return {
      mode: "tutor",
      me: t.id,
      get slots() {
        return (data?.slots ?? []).filter((x) => tutorSees(x, t.id, paired()) !== null);
      },
      get tutors() {
        const ids = new Set((data?.slots ?? []).map((x) => x.tutorId).filter((x): x is string => !!x));
        return [{ id: t.id, name: t.name }, ...[...ids].filter((id) => id !== t.id).map((id) => ({ id, name: name(id) }))];
      },
      get students() {
        return (data?.taught ?? []).filter((x) => x.tutorId === t.id).map((x) => ({ id: x.id, name: x.name, tutorId: t.id }));
      },
      studentName: (id) => name(id) || "Student",
      take: (slot) => takeLesson(t, slot),
      marketPay: (slot) => (slot as TutoringSlot & { marketPayCents?: number }).marketPayCents ?? 0,
      asked: (slot) => waitingFor(t, { slotId: slot.id }),
      withdraw,
      times: (slot, start, end) => step("/api/me/tutoring/times", { slotId: slot.id, start, end }, TT.CAL_TIMES_SAVED),
      done,
      get entries() {
        return (data?.entries ?? []) as TutoringEntry[];
      },
    };
  }
</script>

<div class="page">
  <h1>Tutoring</h1>
  {#if error}<p class="owes">{error}</p>{/if}
  {#if !data}
    <p class="muted">Getting your tutoring from the server.</p>
  {:else}
    {#each data.pending ?? [] as p (p.id)}
      <section class="card ask">
        <h2>Is this you?</h2>
        <p>Miles connected your account to <strong>{p.name}</strong>{p.kind === "tutor" ? " as a tutor" : p.family ? ` (${p.family})` : " as their family"}.</p>
        <div class="btns">
          <button class="btn primary" onclick={() => confirm(p, true)}>Yes, that's {p.kind === "tutor" ? "me" : "us"}</button>
          <button class="btn" onclick={() => confirm(p, false)}>No, not me</button>
        </div>
      </section>
    {/each}
    {#each data.students as s (s.id)}
      {@const sum = studentSummary(s.id, data.entries)}
      {@const upcoming = data.slots.filter((x) => x.studentId === s.id && x.status === "booked" && x.startsAt >= nowIso)}
      <section class="card">
        <header>
          <h2>{s.name}</h2>
          {#if s.grandfathered}<span class="gold" title="You keep your original rate">★ Member since {month(s.since)} · original rate</span>{:else}<span class="muted small">Member since {month(s.since)}</span>{/if}
        </header>
        <div class="tiles">
          {#if sum.boughtMinutes}
            <div class="hl"><span class="muted">Prepaid lessons</span><LessonProgress p={lessonProgress(s.id, data.entries, data.slots)} wide /></div>
          {/if}
          <div><span class="muted">{A.MY_BALANCE_LABEL}</span><strong><BalanceAmount cents={sum.balanceCents} /></strong></div>
          <div><span class="muted">Sessions</span><strong>{sum.sessions}</strong></div>
        </div>
        <div class="rate">
          <span class="best">{formatCents(s.rateCashCents)}/hr <span class="small">Zelle or cash · best price</span></span>
          {#if s.rateCardCents && s.rateCardCents !== s.rateCashCents}<span class="muted">{formatCents(s.rateCardCents)}/hr by card</span>{/if}
        </div>

        {#if s.billing === "package" && s.planMinutes}
          <div class="rate">
            <span>Your plan: <strong>{s.planMinutes / 60} hours</strong></span>
            <span class="best">{formatCents(s.planCents ?? 0)} <span class="small">Zelle or cash</span></span>
            {#if s.planCardCents && s.planCardCents !== s.planCents}<span class="muted">{formatCents(s.planCardCents)} by card</span>{/if}
          </div>
        {:else if data.packages.length}
          <h3>Packages</h3>
          <ul class="pk">
            {#each data.packages as p (p.id)}
              {@const save = packageSavings(p, s.rateCashCents)}
              <li>
                <span><strong>{p.name}</strong> <span class="muted small">{p.amount} {p.unit}</span></span>
                <span class="best">{formatCents(p.priceCashCents)} <span class="small">Zelle/cash</span></span>
                {#if p.priceCardCents}<span class="muted small">{formatCents(p.priceCardCents)} card</span>{/if}
                {#if save}<span class="save">Saves {save}%</span>{/if}
              </li>
            {/each}
          </ul>
          {#if H.MINE_PACKAGE_NOTE.trim()}<p class="muted small">{H.MINE_PACKAGE_NOTE}</p>{/if}
        {/if}

        {#if upcoming.length}
          <h3>Coming up</h3>
          <ul class="list-s">{#each upcoming as x (x.id)}<li>{when(x.startsAt)}{name(x.tutorId) ? ` with ${name(x.tutorId)}` : ""}</li>{/each}</ul>
        {/if}

        <h3>History</h3>
        <ul class="list-s">
          {#each data.entries.filter((e) => e.studentId === s.id).slice().reverse() as e (e.id)}
            <li>
              <span>{shortDate(e.on)} · {e.kind === "session" ? `Session, ${hoursText(e.minutes)}` : e.kind === "purchase" ? `Package: ${e.note}` : e.kind === "payment" ? "Payment" : e.note || e.kind}</span>
              <span class="num">{e.cents ? (e.kind === "payment" ? `−${formatCents(e.cents)}` : formatCents(e.cents)) : ""}</span>
            </li>
          {:else}
            <li class="muted">Nothing yet.</li>
          {/each}
        </ul>
      </section>
    {/each}

    {#each data.tutors as t (t.id)}
      {@const sum = tutorSummary(t, data.entries)}
      {@const mine = (data.taught ?? []).filter((x) => x.tutorId === t.id)}
      {@const grabs = (data.open ?? []).filter((o) => o.tutorId === t.id)}
      {@const mineReqs = (data.claims ?? []).filter((c) => c.tutorId === t.id && c.decidedBy !== "market price").slice(0, 10)}
      <section class="card">
        <header>
          <h2>Teaching</h2>
        </header>
        <!-- The same card Miles sees on Tutors: your own pay, never anyone else's. -->
        <div class="list" aria-label="Your tutoring pay">
          <PersonCard
            overview={personOverview([tutorSource(t, data.entries, lessonLookup(data.slots, data.names))])}
            name={t.name}
            scope="tutoring"
            viewer={{ side: "self" }}
            open={!foldedPay}
            ontoggle={() => (foldedPay = !foldedPay)}
            about={[
              mine.map((m) => m.name).join(", ") || TT.TUTOR_NO_STUDENTS,
              fill(TT.TUTOR_TAUGHT_TEXT, { hours: hoursText(sum.minutes), count: sum.sessions, sessions: sum.sessions === 1 ? TT.TUTOR_SESSION_WORD : TT.TUTOR_SESSIONS_WORD }),
            ]}
          />
        </div>
        {#if mine.length}
          <h3>Your students</h3>
          <!-- The same student cards as Miles's Students page, without rates, families or what they pay. -->
          <div class="list" aria-label="Your students">
            {#each mine as m (m.id)}
              {@const ss = studentSummary(m.id, data.entries)}
              <StudentCard
                name={m.name}
                parts={[m.className, `with ${t.name}`, fill(TT.CLAIM_PAY, { pay: `${formatCents(payOf(t, m.id))}/hr` })]}
                prog={ss.boughtMinutes ? lessonProgress(m.id, data.entries, data.slots) : null}
                sessions={ss.boughtMinutes ? null : ss.sessions}
              />
            {/each}
          </div>
        {/if}

        {#if grabs.length || shows(TT.OPEN_EMPTY)}
          <h3>{TT.OPEN_TITLE}</h3>
          <ul class="list-s" aria-label={TT.OPEN_TITLE}>
            {#each grabs as o (o.id)}
              {@const asked = waitingFor(t, { studentId: o.id })}
              <li>
                <span>
                  <strong>{o.name}</strong>{o.className ? ` · ${o.className}` : ""}
                  {#if o.lessons}<span class="muted small"> · {o.lessons === 1 ? TT.OPEN_LESSONS_ONE : fill(TT.OPEN_LESSONS_MANY, { n: o.lessons })}</span>{/if}
                  <span class="muted small"> · {o.marketPayCents ? fill(TT.CLAIM_PAY, { pay: `${formatCents(o.marketPayCents)}/hr` }) : TT.CLAIM_PAY_UNLISTED}</span>
                </span>
                {#if asked}
                  <span class="btns"><span class="muted small">{TT.CLAIM_WAITING}</span><button class="link-btn" onclick={() => withdraw(asked)}>{TT.CLAIM_WITHDRAW}</button></span>
                {:else}
                  <button class="btn small primary" onclick={() => takeStudent(t, o.id)}>{fill(o.marketPayCents ? TT.CLAIM_TAKE_STUDENT : TT.CLAIM_ASK_STUDENT, { name: o.name })}</button>
                {/if}
              </li>
            {:else}
              <li class="muted">{TT.OPEN_EMPTY}</li>
            {/each}
          </ul>
        {/if}
        {#if mineReqs.length && shows(TT.MY_REQUESTS_TITLE)}
          <h3>{TT.MY_REQUESTS_TITLE}</h3>
          <ul class="list-s">
            {#each mineReqs as c (c.id)}
              {@const sl = c.slotId ? data.slots.find((x) => x.id === c.slotId) : null}
              <li>
                <span>{name(c.studentId) || "Student"}{sl ? `, ${when(sl.startsAt)}` : ""}</span>
                <span class="muted small">{STATUS[c.status]}{c.status === "approved" && c.payCents ? ` · ${fill(TT.CLAIM_PAY, { pay: `${formatCents(c.payCents)}/hr` })}` : ""}</span>
              </li>
            {/each}
          </ul>
        {/if}

        <h3>Your schedule</h3>
        <Calendar source={calendarFor(t)} />
      </section>
    {/each}

    {#if !data.students.length && !data.tutors.length && !data.pending?.length}
      <p class="muted">Nothing here yet. When Miles links your account to a student or as a tutor, it shows up here.</p>
    {/if}
  {/if}
</div>

<style>
  .card {
    display: grid;
    gap: 10px;
    padding: 18px;
    margin-bottom: 16px;
    border-radius: var(--r-lg);
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule);
  }

  header {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }

  h2 {
    margin: 0;
  }

  h3 {
    margin: 8px 0 0;
    font-size: var(--t-md);
  }

  .ask {
    box-shadow: inset 0 0 0 2px var(--focus);
  }

  .ask p {
    margin: 0;
  }

  .btns {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    align-items: center;
  }

  .gold {
    padding: 2px 10px;
    border-radius: 999px;
    background: linear-gradient(135deg, #f7d774, #d9a514);
    color: #4a3500;
    font-size: var(--t-xs);
    font-weight: 700;
  }

  .tiles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px;
  }

  .tiles div {
    display: grid;
    gap: 2px;
    padding: 10px 12px;
    border-radius: var(--r-md);
    background: var(--wash);
  }

  .tiles .hl {
    grid-column: span 2;
    gap: 6px;
  }

  .tiles strong {
    font-size: var(--t-lg);
  }

  .rate {
    display: flex;
    gap: 14px;
    align-items: baseline;
    flex-wrap: wrap;
  }

  .best {
    font-weight: 700;
    color: var(--ok);
  }

  .small {
    font-size: var(--t-xs);
    font-weight: 500;
  }


  .pk,
  .list-s {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 4px;
    font-size: var(--t-sm);
  }

  .pk li,
  .list-s li {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    padding: 6px 0;
    border-top: 1px solid var(--rule);
  }

  .save {
    padding: 1px 8px;
    border-radius: 999px;
    background: color-mix(in srgb, var(--ok) 14%, transparent);
    color: var(--ok);
    font-size: var(--t-xs);
    font-weight: 700;
  }
</style>
